import React, { useEffect, useContext } from 'react';
import { useTranslate } from 'react-admin';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ListIcon from '@material-ui/icons/FormatListBulleted';
import CalendarIcon from '@material-ui/icons/CalendarToday';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import dataProvider from '../../dataProvider';
import {
  fetchRecurringScheduleDetails,
  getAllClientLocations,
  getAllScheduleTypes,
} from './scheduleApiHelpers';
import RecurringSchedule from './RecurringSchedule';
import SingleDayAvailabilityForm from './SingleDayAvailabilityForm';
import { CalendarEditAvailabilityContext } from '../../Context';
import CalendarLegend from './CalendarLegend';
import AvailabilityCalendar from './AvailabilityCalendar';
import AdminNotes from '../AdminNotes';
import ExceptionsTable from './ExceptionsTable';

const useStyles = makeStyles(() => ({
  gridBase: {
    padding: '5px 15px',
    display: 'flex',
    justifyContent: 'center',
  },
  gridItem: {
    padding: '0 !important',
    marginRight: '25px',
    maxWidth: '1300px',
  },
  calendarGridItem: {
    maxWidth: '510px',
  },
  gridHeader: {
    padding: '14px 0 0 14px !important',
    margin: '0',
    width: '120%',
  },
  gridSubtitle: {
    padding: '0 0 0 14px !important',
    color: 'gray',
    fontSize: '12px',
    width: '120%',
    marginBottom: '8px',
  },
  pickerRoot: {
    marginTop: '10px',
    '& .MuiPickersToolbar-toolbar': {
      display: 'none',
    },
    '& .MuiPickersBasePicker-containerLandscape': {
      flexDirection: 'column',
    },
    '& .MuiPickersBasePicker-container': {
      justifyContent: 'center',
      alignItems: 'center',
      height: '300px',
    },
    '& .MuiPickersBasePicker-pickerView': {
      justifyContent: 'flex-start',
    },
  },
  singleAvailabilityForm: {
    padding: '0 10px',
    backgroundColor: '#f2f2f2',
    width: '140%',
    marginLeft: '-16%',
  },
  listIcon: {
    cursor: 'pointer',
    float: 'right',
    marginRight: '10px',
  },
}));

const Scheduling = () => {
  const {
    selectedVehicle,
    scheduleData,
    setRecurringDayDetails,
    setAllScheduleTypes,
    setAllClientLocations,
    setScheduleExceptions,
    refreshExceptionTable,
    setRefreshExceptionTable,
    selectedDate,
    hasWorkplaceService,
    showExceptionsList,
    setShowExceptionsList,
  } = useContext(CalendarEditAvailabilityContext);
  const classes = useStyles();
  const {
    id: serviceVehicleId,
    is_service_bay: isServiceBay,
  } = selectedVehicle;

  const translate = useTranslate();

  useEffect(() => {
    getAllScheduleTypes(translate).then((scheduleTypes) => {
      setAllScheduleTypes(scheduleTypes);
    });
    if (hasWorkplaceService) {
      getAllClientLocations().then((clientLocations) => {
        setAllClientLocations(clientLocations);
      });
    }
  }, [hasWorkplaceService]);

  useEffect(() => {
    fetchRecurringScheduleDetails(selectedVehicle, scheduleData).then(
      (recurringSchedules) => {
        setRecurringDayDetails(recurringSchedules);
      },
    );
    dataProvider
      .getList('schedule-exceptions', {
        filter: {
          service_vehicle_id: serviceVehicleId,
          start_time_after: moment().format(),
          sorted: true,
        },
        sort: {},
        pagination: { page: 1, perPage: 1000 },
      })
      .then(({ data: exceptions }) => {
        setScheduleExceptions(exceptions);
        if (refreshExceptionTable) {
          setRefreshExceptionTable(false);
        }
      });
  }, [refreshExceptionTable, scheduleData, selectedVehicle]);

  const formData =
    scheduleData.find((datum) =>
      moment(moment(datum.start_time).format('MM/DD/YYYY')).isSame(
        moment(selectedDate).format('MM/DD/YYYY'),
      ),
    ) || {};

  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  return (
    <Grid container xs={12} spacing={1} className={classes.gridBase}>
      <Grid item lg={6} md={10} xs={12} className={classes.gridItem}>
        <Typography variant="h3" gutterBottom className={classes.gridHeader}>
          Weekly Hours of Operation
        </Typography>
        <Typography
          variant="subtitle1"
          gutterBottom
          className={classes.gridSubtitle}
        >
          Your weekly recurring availability
        </Typography>
        <RecurringSchedule />
      </Grid>
      {isLarge && <Divider orientation="vertical" flexItem />}
      <Grid item md={5} xs={12} className={classes.gridItem}>
        <Grid
          container
          xs={12}
          display="flex"
          style={{ justifyContent: 'center' }}
        >
          <Grid
            item
            md={8}
            xs={12}
            className={`${classes.gridItem} ${classes.calendarGridItem}`}
          >
            <Typography
              variant="h3"
              gutterBottom
              className={classes.gridHeader}
            >
              Changes to Hours of Operation
            </Typography>
            <Typography
              variant="subtitle1"
              gutterBottom
              className={classes.gridSubtitle}
            >
              Dates when your availability is different than your weekly hours
              of operation
            </Typography>

            {showExceptionsList ? (
              <Tooltip title="Calendar View">
                <CalendarIcon
                  onClick={() => setShowExceptionsList(false)}
                  className={classes.listIcon}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Show List of Exceptions">
                <ListIcon
                  onClick={() => setShowExceptionsList(true)}
                  className={classes.listIcon}
                />
              </Tooltip>
            )}
            <Grid container direction="column">
              <Grid item className={classes.pickerRoot}>
                {showExceptionsList ? (
                  <ExceptionsTable />
                ) : (
                  <>
                    <AvailabilityCalendar />
                    <CalendarLegend />
                  </>
                )}
              </Grid>
              <Grid item>
                {selectedDate && (
                  <>
                    <Box className={classes.singleAvailabilityForm}>
                      <SingleDayAvailabilityForm
                        key={`${serviceVehicleId}-${selectedDate}-${formData.id}`}
                        formData={formData}
                        default_schedule_types={
                          selectedVehicle.default_schedule_types.length > 0
                            ? selectedVehicle.default_schedule_types
                            : null
                        }
                        isServiceBay={isServiceBay}
                      />
                    </Box>
                    <AdminNotes
                      selectedDate={moment(selectedDate).format('YYYY-MM-DD')}
                      service_vehicle_id={serviceVehicleId}
                      isCompact
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Scheduling;
